import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../common/Button";
import { DisplayValue } from "../../common/DisplayValue";
import { FormInput } from "../../common/FormLayout";
import { LabelParam } from "../../common/LabelParam";

import { apiFetchRoute } from "../../../api/apiFetchRoute";
import { enumcountryToI18nKey } from "../../../generated/enums";
import { RequestTypeOf } from "../../../generated/routes";
import { Breeding } from "../../../generated/types";
import { DetailLevel, sectionToIcon } from "../../type/default";

export const BreedingSection = ({
  breeding,
  detailLevel = "default",
  onClick,
  onUpdate,
}: {
  breeding: Breeding;
  detailLevel?: DetailLevel;
  onClick?: () => void;
  onUpdate?: () => void;
}) => {
  const { t } = useTranslation();
  // Affiche l'enregistrement en attendant que le parent renvoie les données à jour.
  const [savedBreeding, setSavedBreeding] = useState<Breeding | null>(null);
  const theBreeding = savedBreeding ?? breeding;
  const [isEditing, setIsEditing] = useState(false);
  const [editedBreeding, setEditedBreeding] = useState(breeding);
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    setSavedBreeding(null);
  }, [breeding]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditedBreeding((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    setSaveFailed(false);
    try {
      type BreedingRequest = RequestTypeOf<"putBreedings">;

      const body: BreedingRequest = editedBreeding;
      const response = await apiFetchRoute("putBreedings", body);

      if (!response.ok) {
        throw new Error("error-breeding-update-failed");
      }
      setIsEditing(false);
      setSavedBreeding(editedBreeding);
      onUpdate?.();
    } catch (error) {
      setSaveFailed(true);
      console.error(t("error-breeding-update-failed"), error);
    }
  };

  const startEditing = () => {
    setEditedBreeding(theBreeding);
    setSaveFailed(false);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setSaveFailed(false);
    setIsEditing(false);
  };

  return (
    <div>
      <div className="flex justify-between border-b border-grey-light mb-4">
        <div className="flex gap-4 items-center">
          {detailLevel === "maxi" && (
            <FontAwesomeIcon icon={sectionToIcon.section_breeding} />
          )}
          <button
            className={`${detailLevel === "mini" ? "text-sm" : detailLevel === "maxi" ? "text-lg" : "text-base"} font-oxaniumMedium ${
              onClick ? "hover:text-tertiary cursor-pointer" : "cursor-default"
            }`}
            onClick={onClick}
          >
            {theBreeding.name}
          </button>
        </div>
        {detailLevel === "maxi" && !isEditing && (
          <button
            onClick={startEditing}
            className="text-grey-medium hover:text-secondary"
          >
            <FontAwesomeIcon icon="pen-to-square" className="h-5 w-5" />
          </button>
        )}
      </div>

      <div className="space-y-4">
        {detailLevel !== "mini" && (
          <>
            {detailLevel === "maxi" &&
              (isEditing ? (
                <LabelParam label={t("breeding-name")}>
                  <FormInput
                    value={editedBreeding.name || ""}
                    onChange={handleChange}
                    name="name"
                    sizeInput="small"
                    className="bg-transparent border-none p-0 text-sm"
                  />
                </LabelParam>
              ) : (
                <LabelParam label={t("breeding-name")}>
                  <DisplayValue value={theBreeding.name ?? ""} />
                </LabelParam>
              ))}

            {isEditing ? (
              <LabelParam label={t("company-address")}>
                <FormInput
                  value={editedBreeding.address || ""}
                  onChange={handleChange}
                  name="address"
                  sizeInput="small"
                  className="bg-transparent border-none p-0 text-sm"
                />
              </LabelParam>
            ) : (
              <LabelParam label={t("company-address")}>
                <DisplayValue value={theBreeding.address ?? ""} />
              </LabelParam>
            )}
            {breeding.address_complementary &&
              (isEditing ? (
                <LabelParam label={t("company-address-complementary")}>
                  <FormInput
                    value={editedBreeding.address_complementary || ""}
                    onChange={handleChange}
                    name="address_complementary"
                    sizeInput="small"
                    className="bg-transparent border-none p-0 text-sm"
                  />
                </LabelParam>
              ) : (
                <LabelParam label={t("company-address-complementary")}>
                  <DisplayValue
                    value={theBreeding.address_complementary ?? ""}
                  />
                </LabelParam>
              ))}

            {isEditing ? (
              <LabelParam label={t("company-city")}>
                <FormInput
                  value={editedBreeding.city || ""}
                  onChange={handleChange}
                  name="city"
                  sizeInput="small"
                  className="bg-transparent border-none p-0 text-sm"
                />
              </LabelParam>
            ) : (
              <LabelParam label={t("company-city")}>
                <DisplayValue value={theBreeding.city ?? ""} />
              </LabelParam>
            )}

            {isEditing ? (
              <LabelParam label={t("company-zip-code")}>
                <FormInput
                  value={editedBreeding.zip_code || ""}
                  onChange={handleChange}
                  name="zip_code"
                  sizeInput="small"
                  className="bg-transparent border-none p-0 text-sm"
                />
              </LabelParam>
            ) : (
              <LabelParam label={t("company-zip-code")}>
                <DisplayValue value={theBreeding.zip_code ?? ""} />
              </LabelParam>
            )}

            {/* Liste fermée de codes à deux lettres : pas de saisie libre. */}
            <LabelParam label={t("company-country")}>
              <DisplayValue
                value={
                  theBreeding.country
                    ? t(enumcountryToI18nKey[theBreeding.country])
                    : "-"
                }
              />
            </LabelParam>
          </>
        )}

        {detailLevel === "maxi" && (
          <div className="space-y-4">
            <LabelParam label={t("breeding-id")}>
              <DisplayValue value={theBreeding.id ?? ""} />
            </LabelParam>
          </div>
        )}

        {isEditing && saveFailed && (
          <p className="text-xs text-warning-readable">
            {t("ticket-update-error")}
          </p>
        )}
        {isEditing && (
          <div className="flex justify-end gap-4">
            <Button type="button" variant="secondary" onClick={handleCancel}>
              {t("button-cancel")}
            </Button>
            <Button type="submit" onClick={handleSubmit}>
              {t("button-save")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
